import { IScannerCode } from "./Abstraction/IScanner";
import { BaseCameraScanner, IBaseCameraScannerOptions } from "./BaseCameraScanner";
import { addClass, delayAsync, element, removeClass } from "./Utils";
import { createElement, ReactElement, ReactNode } from "react";
import CameraScannerPreview from "./Components/CameraScannerPreview";

export interface IBaseFrameScannerOptions extends IBaseCameraScannerOptions {
    frameDelay?: number;
}

export abstract class BaseFrameScanner extends BaseCameraScanner {

    protected _canvas: HTMLCanvasElement;
    protected _frameDelay: number;

    constructor(options?: IBaseFrameScannerOptions) {
        super(options);
        this._canvas = document.createElement("canvas");
        this._frameDelay = options.frameDelay ?? 300;
    }

    /**************************************/

    protected getVideoWork() {
        return element<HTMLVideoElement>("#preview");
    }

    /**************************************/

    protected async showScannerAsync() {

        console.debug("showScannerAsync");

        const preview = element(".scan-preview");
        removeClass(preview, "hidden");

        await delayAsync();
        addClass(preview, "visible");
    }

    /**************************************/

    protected async stopCamera() {

        const video = this.getVideo();

        if (video) {

            video.pause();

            if (video.srcObject)
                (video.srcObject as MediaStream).getTracks()[0].stop();

            video.srcObject = null;
        }
    }     

    /**************************************/

    protected async hideScannerAsync() {

        this.stopCamera();

        const preview = element(".scan-preview");

        if (preview) {

            removeClass(preview, "visible");

            await delayAsync(500);

            if (!this._isScanning)
                addClass(preview, "hidden");
        }
    }

    /**************************************/

    protected async updateCameraAsync() {

        this.stopCamera();

        const video = this.getVideo();

        if (video) {

            const mediaDevice = await navigator.mediaDevices.getUserMedia({ video: { deviceId: this._cameras[this._activeCamera].deviceId } });

            video.srcObject = mediaDevice;

            video.play();

            await this.waitForScannerAsync();

            if (this._isScanning) 
                await this.updateCapsAsync();
        }
    }

    /**************************************/

    protected async scanFrame() {

        const video = this.getVideo();
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (width == 0 || height == 0)
            return;

        this._canvas.width = width;
        this._canvas.height = height;
        const ctx = this._canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, width, height);

        const imgData = ctx.getImageData(0, 0, width, height);

        return await this.scanFrameWorkAsync(imgData)
    }

    /**************************************/

    protected abstract scanFrameWorkAsync(image: ImageData): Promise<IScannerCode>;

    /**************************************/

    async scanSingleAsync() {

        console.debug("START scanSingleAsync");

        this._isScanning = true;

        let scanCode: IScannerCode;

        if (isNaN(this._activeCamera) || this._activeCamera < 0 || this._activeCamera >= this._cameras?.length)
            this._activeCamera = 0;

        await this.showScannerAsync();

        await this.updateCameraAsync();

        while (this._isScanning) {

            const curCamera = this._activeCamera;

            try {

                await delayAsync(0);

                scanCode = await this.scanFrame();

                if (scanCode) { 
                    console.debug("RESULT scanSingleAsync");
                    break;
                }

                await delayAsync(this._frameDelay);

            }
            catch (ex) {
                console.warn(ex);
                if (curCamera == this._activeCamera)
                    break;
            }
        }

        console.debug("STOPPING scanSingleAsync");

        await this.stopScanAsync();

        console.debug("DONE scanSingleAsync");

        return scanCode;
    }

    /**************************************/

    async stopScanAsync() {

        console.debug("stopScanAsync");

        if (!this._isScanning) {
            console.warn("not scanning");
            return;
        }

        await this.hideScannerAsync();

        this._isScanning = false;

        console.debug("stopped");
    }

    /**************************************/

    async setCameraAsync(index: number) {

        this._activeCamera = index;

        console.log("Change Active camera: ", this._activeCamera, " of ", this._cameras.length);

        if (this._isScanning)
            await this.updateCameraAsync();
    }

    /**************************************/

    createPreview(info?: ReactNode, isScanning?: boolean): ReactElement {
        return createElement(CameraScannerPreview, { scanner: this, info, isActive: isScanning, useCanvas: this._useCanvas });
    }
}

